import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  PRICE_METHODS,
  TAX_RULES,
  WEIGHT_TYPES,
  useStoreAisles,
  useStoreSections,
  type BusinessItemAssociation,
} from './item-prices.api';

/**
 * What a store says about one item it stocks.
 *
 * The same forty-odd fields are needed whether a new item is being requested or
 * an existing one is being tied to this price, so they live here once. In the
 * page this replaces they were written out twice, several hundred lines apart,
 * and the two copies had already diverged.
 */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <Label className="text-sm font-normal">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

export function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** The twelve dietary claims, which are just booleans in a grid. */
const DIETARY: {
  key: keyof BusinessItemAssociation;
  labelKey: string;
}[] = [
  { key: 'isAllNatural', labelKey: 'itemPrices.natural' },
  { key: 'isLactoseFree', labelKey: 'itemPrices.lactoseFree' },
  { key: 'isSugarFree', labelKey: 'itemPrices.sugarFree' },
  { key: 'isKeto', labelKey: 'itemPrices.keto' },
  { key: 'isOrganic', labelKey: 'itemPrices.organic' },
  { key: 'isVegan', labelKey: 'itemPrices.vegan' },
  { key: 'isVegetarian', labelKey: 'itemPrices.vegetarian' },
  { key: 'isGlutenFree', labelKey: 'itemPrices.glutenFree' },
  { key: 'isGrassFed', labelKey: 'itemPrices.grassFed' },
  { key: 'isAntioxidant', labelKey: 'itemPrices.antioxidant' },
  { key: 'isNutFree', labelKey: 'itemPrices.nutFree' },
  { key: 'isNonGmo', labelKey: 'itemPrices.nonGmo' },
];

export function AssociationFields({
  value,
  onChange,
  businessId,
}: {
  value: BusinessItemAssociation;
  onChange: (patch: Partial<BusinessItemAssociation>) => void;
  businessId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';

  const sections = useStoreSections(businessId);
  const aisles = useStoreAisles(businessId);

  const number = (key: keyof BusinessItemAssociation) => ({
    type: 'number' as const,
    value: (value[key] as number | null) ?? '',
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange({
        [key]: event.target.value === '' ? null : Number(event.target.value),
      } as Partial<BusinessItemAssociation>),
  });

  return (
    <div className="space-y-6">
      <Section title={t('itemPrices.whereItSits')}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('itemPrices.inStoreId')}>
            <Input
              value={value.inStoreId ?? ''}
              onChange={(e) => onChange({ inStoreId: e.target.value })}
            />
          </Field>
          <Field label="UPC">
            <Input
              value={value.upc ?? ''}
              onChange={(e) => onChange({ upc: e.target.value })}
            />
          </Field>
        </div>

        <Field label={t('itemPrices.category')}>
          {sections.isLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Select
              value={value.sectionId ?? ''}
              onValueChange={(sectionId) => onChange({ sectionId })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('itemPrices.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                {(sections.data ?? []).map((section) => (
                  <SelectItem key={section.id} value={section.id}>
                    {(isEnglish ? section.nameEng : section.nameSpa) || '—'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('itemPrices.department')}>
            <Input
              value={value.department ?? ''}
              onChange={(e) => onChange({ department: e.target.value })}
            />
          </Field>
          <Field label={t('itemPrices.aisle')}>
            {/* A free-text field with the store's known aisles behind it, so a
                typo does not silently create a thirteenth aisle. */}
            <Input
              list="store-aisles"
              value={value.aisle ?? ''}
              onChange={(e) => onChange({ aisle: e.target.value })}
            />
            <datalist id="store-aisles">
              {(aisles.data ?? []).map((aisle) => (
                <option key={aisle} value={aisle} />
              ))}
            </datalist>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Toggle
            label={t('itemPrices.isVisible')}
            checked={value.isVisible}
            onChange={(isVisible) => onChange({ isVisible })}
          />
          <Toggle
            label={t('itemPrices.isAvailable')}
            checked={value.isAvailable}
            onChange={(isAvailable) => onChange({ isAvailable })}
          />
        </div>
      </Section>

      <Section title={t('itemPrices.pricing')}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('itemPrices.basePrice')}>
            <Input step="0.01" {...number('basePrice')} />
          </Field>
          <Field label={t('itemPrices.baseMultiple')}>
            <Input {...number('baseMultiple')} />
          </Field>
        </div>

        <Field label={t('itemPrices.basePriceMethod')}>
          <Select
            value={String(value.basePriceMethod ?? 0)}
            onValueChange={(next) => onChange({ basePriceMethod: Number(next) })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRICE_METHODS.map((method) => (
                <SelectItem key={method.value} value={String(method.value)}>
                  {t(method.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('itemPrices.cost')}>
            <Input step="0.01" {...number('cost')} />
          </Field>
          <Field label={t('itemPrices.pack')}>
            <Input {...number('pack')} />
          </Field>
        </div>

        <Field label={t('itemPrices.taxRuleLabel')}>
          <Select
            value={String(value.taxRule ?? 0)}
            onValueChange={(next) => onChange({ taxRule: Number(next) })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAX_RULES.map((rule) => (
                <SelectItem key={rule.value} value={String(rule.value)}>
                  {t(rule.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`${t('itemPrices.stateTax')} %`}>
            <Input step="0.01" {...number('stateTaxPct')} />
          </Field>
          <Field label={`${t('itemPrices.municipalTax')} %`}>
            <Input step="0.01" {...number('munTaxPct')} />
          </Field>
        </div>
      </Section>

      <Section title={t('itemPrices.promo')}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('itemPrices.promoPrice')}>
            <Input step="0.01" {...number('promoPrice')} />
          </Field>
          <Field label={t('itemPrices.promoMultiple')}>
            <Input {...number('promoMultiple')} />
          </Field>
        </div>

        <Field label={t('itemPrices.promoPriceMethod')}>
          <Select
            value={String(value.promoPriceMethod ?? 0)}
            onValueChange={(next) =>
              onChange({ promoPriceMethod: Number(next) })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRICE_METHODS.map((method) => (
                <SelectItem key={method.value} value={String(method.value)}>
                  {t(method.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('itemPrices.promoStartDate')}>
            <Input
              type="date"
              value={(value.promoStartDate ?? '').slice(0, 10)}
              onChange={(e) =>
                onChange({ promoStartDate: e.target.value || null })
              }
            />
          </Field>
          <Field label={t('itemPrices.promoEndDate')}>
            <Input
              type="date"
              value={(value.promoEndDate ?? '').slice(0, 10)}
              onChange={(e) =>
                onChange({ promoEndDate: e.target.value || null })
              }
            />
          </Field>
        </div>
      </Section>

      <Section title={t('itemPrices.howItIsSold')}>
        <Toggle
          label={t('itemPrices.randomWeight')}
          checked={value.hasRandomWeight}
          onChange={(hasRandomWeight) => onChange({ hasRandomWeight })}
        />

        <Field label={t('itemPrices.weightTypeLabel')}>
          <Select
            value={String(value.weightType ?? 0)}
            // Only a random-weight item is sold by weight, so the unit is not
            // a question until then.
            disabled={!value.hasRandomWeight}
            onValueChange={(next) => onChange({ weightType: Number(next) })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEIGHT_TYPES.map((type) => (
                <SelectItem key={type.value} value={String(type.value)}>
                  {t(type.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('itemPrices.minimum')}>
            <Input step="0.01" {...number('minQuantity')} />
          </Field>
          <Field label={t('itemPrices.maximum')}>
            <Input step="0.01" {...number('maxQuantity')} />
          </Field>
          <Field label={t('itemPrices.stepAmount')}>
            <Input step="0.01" {...number('stepAmount')} />
          </Field>
          <Field label={t('itemPrices.unitPerPackage')}>
            <Input step="0.01" {...number('unitPriceFactor')} />
          </Field>
        </div>

        <Toggle
          label={t('itemPrices.madeLocally')}
          checked={value.madeLocally}
          onChange={(madeLocally) => onChange({ madeLocally })}
        />
      </Section>

      <Section title={t('itemPrices.dietary')}>
        <div className="grid gap-x-4 sm:grid-cols-2">
          {DIETARY.map((claim) => (
            <Toggle
              key={claim.key}
              label={t(claim.labelKey)}
              checked={!!value[claim.key]}
              onChange={(next) =>
                onChange({ [claim.key]: next } as Partial<BusinessItemAssociation>)
              }
            />
          ))}
        </div>
      </Section>
    </div>
  );
}
